import { useState } from "react";

import type { Column } from "@/components/shared/data-table";
import { DataTable } from "@/components/shared/data-table";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useHasPermission, useMe } from "@/hooks/use-auth";
import { useRiderPayrolls } from "@/hooks/use-rider-payrolls";
import { useServerTable } from "@/hooks/use-server-table";
import { useWorkspaceFilterOption } from "@/hooks/use-workspace-filter";
import { useWorkspaces } from "@/hooks/use-workspaces";
import { queryKeys } from "@/lib/query-keys";
import { formatCurrency } from "@/lib/utils";
import { riderPayrollService } from "@/services/rider-payroll";

import { RiderPayrollDialog } from "./rider-payroll-dialog";
import { RIDER_PAYROLL_STATUS_LABELS } from "./rider-payroll-labels";

import type { RiderPayroll, RiderPayrollStatus } from "@artisancode/api-types";

export function RiderPayrollList() {
  const canCreate = useHasPermission("rider_payrolls.create");
  const [open, setOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: me } = useMe();
  const { data: workspacesData } = useWorkspaces({ per_page: 100 });
  const workspaces = workspacesData?.items ?? [];

  function workspaceName(workspaceId: string) {
    return workspaces.find((w) => w.id === workspaceId)?.name ?? "-";
  }

  const workspaceFilter = useWorkspaceFilterOption();
  const table = useServerTable<RiderPayroll>({
    queryKey: (params) => queryKeys.riderPayrolls.list(params),
    fetcher: (params) => riderPayrollService.list(params),
    pageSize: 10,
    filterKeys: ["status", "date_from", "date_to", "workspace_id"],
  });

  const { data: liveData } = useRiderPayrolls();
  const editing =
    liveData?.items.find((r) => r.id === openId) ??
    table.items.find((r) => r.id === openId) ??
    null;

  const columns: Column<RiderPayroll>[] = [
    {
      key: "period",
      label: "Periode",
      render: (r) => (
        <div className="flex flex-col">
          <div className="flex items-center gap-1.5">
            <span className="font-medium">{r.riderName}</span>
            {r.transactionCode && (
              <Badge
                variant="outline"
                className="font-mono text-[10px] px-1.5 py-0 bg-muted/40 text-foreground"
              >
                {r.transactionCode}
              </Badge>
            )}
          </div>
          <span className="text-xs text-muted-foreground">
            {r.periodStart} s/d {r.periodEnd}
          </span>
        </div>
      ),
    },
    ...(me?.canSwitchWorkspace
      ? [
          {
            key: "workspace",
            label: "Workspace",
            render: (r: RiderPayroll) => (
              <span className="text-sm">{workspaceName(r.workspaceId)}</span>
            ),
          },
        ]
      : []),
    {
      key: "daysWorked",
      label: "Hari Kerja",
      render: (r) => <span className="text-sm">{r.daysWorked} hari</span>,
    },
    {
      key: "netAmount",
      label: "Gaji Bersih",
      render: (r) => (
        <span className="font-medium">{formatCurrency(r.netAmount)}</span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (r) => (
        <Badge
          variant={
            r.status === "paid"
              ? "default"
              : r.status === "cancelled"
                ? "destructive"
                : "outline"
          }
        >
          {RIDER_PAYROLL_STATUS_LABELS[r.status]}
        </Badge>
      ),
    },
  ];

  const statusOptions: { label: string; value: RiderPayrollStatus }[] = [
    { label: "Draft", value: "draft" },
    { label: "Dibayar", value: "paid" },
    { label: "Dibatalkan", value: "cancelled" },
  ];

  return (
    <div>
      <PageHeader
        title="Penggajian Rider"
        description="Hitung gaji harian, pendapatan penjualan, dan bonus rider per periode."
        action={
          canCreate && (
            <Button
              onClick={() => {
                setOpenId(null);
                setOpen(true);
              }}
            >
              Buat Penggajian
            </Button>
          )
        }
      />
      <DataTable
        data={table.items}
        loadedData={table.loadedItems}
        columns={columns}
        searchPlaceholder="Cari rider..."
        query={table.query}
        onQueryChange={table.onQueryChange}
        activeFilters={table.activeFilters}
        onFilterChange={table.onFilterChange}
        filters={[
          ...workspaceFilter,
          { key: "status", label: "Status", options: statusOptions },
        ]}
        dateRange={{
          from: table.activeFilters.date_from ?? "",
          to: table.activeFilters.date_to ?? "",
          onChange: (date_from, date_to) =>
            table.onFiltersChange({ date_from, date_to }),
        }}
        page={table.page}
        totalPages={table.totalPages}
        totalCount={table.totalCount}
        onPageChange={table.onPageChange}
        hasMore={table.hasMore}
        onLoadMore={table.onLoadMore}
        loading={table.loading}
        actions={(item) => (
          <button
            type="button"
            className="text-sm text-primary underline-offset-2 hover:underline"
            onClick={() => {
              setOpenId(item.id);
              setOpen(true);
            }}
          >
            Detail
          </button>
        )}
        mobileRender={(r) => (
          <Card className="overflow-hidden border border-border shadow-xs">
            <div className="p-3.5 space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-semibold text-sm">{r.riderName}</span>
                  {r.transactionCode && (
                    <Badge
                      variant="outline"
                      className="font-mono text-[10px] px-1.5 py-0 bg-muted/40 text-foreground"
                    >
                      {r.transactionCode}
                    </Badge>
                  )}
                </div>
                <Badge
                  variant={
                    r.status === "paid"
                      ? "default"
                      : r.status === "cancelled"
                        ? "destructive"
                        : "outline"
                  }
                  className="text-[11px] px-2 py-0.5"
                >
                  {RIDER_PAYROLL_STATUS_LABELS[r.status]}
                </Badge>
              </div>

              <p className="text-xs text-muted-foreground">
                {r.periodStart} s/d {r.periodEnd} · {r.daysWorked} hari kerja
              </p>

              <div className="rounded-lg bg-muted/40 p-2.5">
                <span className="text-[11px] text-muted-foreground block">
                  Gaji Bersih
                </span>
                <span className="font-bold text-sm">
                  {formatCurrency(r.netAmount)}
                </span>
              </div>

              <div className="flex justify-end pt-1 border-t border-border/40">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs font-medium text-primary hover:bg-primary/10"
                  onClick={() => {
                    setOpenId(r.id);
                    setOpen(true);
                  }}
                >
                  Detail →
                </Button>
              </div>
            </div>
          </Card>
        )}
      />

      <RiderPayrollDialog
        open={open}
        onOpenChange={setOpen}
        editing={editing}
      />
    </div>
  );
}
